import { SpaceAddressFromSpaceId } from '@towns-protocol/web3';
import { decodeAbiParameters } from 'viem';
import { getBlockNumber } from 'viem/actions';
import { convertBlockTagToRpc } from '../utils/convertBlockTag';
import { prepareSwapCalls } from '../operations/executeSwap';
const MAX_SIMULATION_RETRIES = 3;
/**
 * Simulate asset changes using Alchemy's bundle simulation
 * Much faster than UserOp simulation for quote generation
 *
 * Note: This method logs simulation errors but doesn't throw for them.
 * Returns all asset changes including successful and failed simulations.
 */
export async function simulateAssetChangesBundle(smartAccount, params) {
    // Convert calls to Alchemy's transaction format
    const transactions = params.calls.map((call) => ({
        from: params.walletAddress,
        to: call.to,
        data: call.data,
        value: call.value > 0n ? `0x${call.value.toString(16)}` : undefined,
    }));
    // Use alchemy_simulateAssetChangesBundle directly
    const results = await smartAccount.publicRpcClient.request({
        method: 'alchemy_simulateAssetChangesBundle',
        params: [transactions, convertBlockTagToRpc(params.blockTag)],
    });
    // Check for errors in any of the responses and log them for debugging
    const allErrors = results.filter((result) => result.error).map((result) => result.error);
    if (allErrors.length > 0) {
        await logSimulationError(smartAccount, 'alchemy_simulateAssetChangesBundle', params, {
            errorCount: allErrors.length,
            errors: allErrors,
        });
    }
    // Flatten all changes from all transactions (similar to execution bundle)
    return results.flatMap((r) => r.changes);
}
/**
 * Simulate multiple calls using eth_simulateV1
 * Returns the native eth_simulateV1 response with proper Viem typing
 */
export async function simulateCalls(publicClient, params) {
    // Convert PartialCall format to eth_simulateV1 format
    const calls = params.calls.map((call) => ({
        from: params.walletAddress,
        to: call.to,
        data: call.data,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        ...(call.value > 0n && { value: `0x${call.value.toString(16)}` }),
    }));
    // Use eth_simulateV1 RPC method - TypeScript will infer the proper return type
    return publicClient.request({
        method: 'eth_simulateV1',
        params: [
            {
                blockStateCalls: [{ calls }],
            },
            convertBlockTagToRpc(params.blockTag),
        ],
    });
}
export async function simulateSwapOp(publicClient, params) {
    const calls = prepareSwapCalls(params);
    return simulateSwapExecutionBundle(publicClient, {
        calls,
        ...params,
    });
}
/**
 * Simulate calls with retry logic for network reliability
 */
async function simulateCallsWithRetries(publicClient, params) {
    let lastError = null;
    for (let attemptsLeft = MAX_SIMULATION_RETRIES; attemptsLeft > 0; attemptsLeft--) {
        try {
            return await simulateCalls(publicClient, params);
        }
        catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            if (attemptsLeft === 1) {
                throw lastError;
            }
        }
    }
    throw lastError || new Error('Simulation failed after all retries');
}
/**
 * Swap-specific execution bundle simulation
 * Decodes swap output amount from execution traces
 *
 * Uses eth_call for single calls and eth_simulateV1 for multi-call bundles.
 */
async function simulateSwapExecutionBundle(publicClient, params) {
    // Get the swap contract address (space or router)
    const swapContractAddress = params.spaceId !== undefined
        ? SpaceAddressFromSpaceId(params.spaceId).toLowerCase()
        : params.swapRouterAddress.toLowerCase();
    let outputData;
    // For single calls, use eth_call directly for optimal performance
    if (params.calls.length === 1) {
        const call = params.calls[0];
        if (call.to.toLowerCase() !== swapContractAddress) {
            throw new Error('Single call is not to the swap contract');
        }
        const { data } = await publicClient.call({
            account: params.walletAddress,
            ...call,
        });
        if (!data) {
            throw new Error('eth_call returned no data');
        }
        outputData = data;
    }
    else {
        // For multi-call bundles, use eth_simulateV1
        const result = await simulateCallsWithRetries(publicClient, params);
        // Extract return data from eth_simulateV1
        // We only simulate one block state, so we take the first (and only) result
        const blockResult = result[0];
        if (!blockResult) {
            throw new Error('eth_simulateV1 returned empty result');
        }
        const swapCall = blockResult.calls.find((_, index) => params.calls[index]?.to.toLowerCase() === swapContractAddress);
        if (!swapCall?.returnData) {
            throw new Error('Swap call not found or has no returnData in eth_simulateV1');
        }
        if (swapCall.error) {
            throw new Error(`Swap transaction reverted: ${swapCall.error.message}\nreturnData: ${swapCall.returnData}`);
        }
        outputData = swapCall.returnData;
    }
    // Shared decoding logic for both single call and multi-call paths
    if (!outputData) {
        throw new Error('No output data available for decoding');
    }
    try {
        // Always decode with uint256 - amountOut is always the first return value
        const decoded = decodeAbiParameters([{ type: 'uint256' }], outputData);
        return decoded[0];
    }
    catch (error) {
        throw new Error(`Failed to decode swap output: ${error instanceof Error ? error.message : String(error)}`);
    }
}
/**
 * Logs simulation errors with both error details and user operation context for Tenderly debugging
 */
async function logSimulationError(smartAccount, method, params, additionalData = {}) {
    const baseLogData = {
        walletAddress: params.walletAddress,
        calls: params.calls.map((call) => ({
            to: call.to,
            value: call.value.toString(),
            data: call.data,
        })),
        ...additionalData,
    };
    try {
        if (smartAccount?.client.account) {
            const [encodedCallData, blockNumber] = await Promise.all([
                smartAccount.client.account.encodeCalls(params.calls.map((call) => ({
                    to: call.to,
                    value: call.value || 0n,
                    data: call.data,
                }))),
                getBlockNumber(smartAccount.client),
            ]);
            const logDataWithContext = {
                tenderlyCall: {
                    blockNumber: blockNumber.toString(),
                    from: smartAccount.entrypointAddress,
                    to: params.walletAddress,
                    data: encodedCallData,
                },
                smartAccountAddress: params.walletAddress,
                entryPointAddress: smartAccount.entrypointAddress,
                ...baseLogData,
            };
            console.error(`${method} failed. Parameters for reproduction:`, JSON.stringify(logDataWithContext, null, 2));
        }
        else {
            console.error(`${method} failed. Parameters for reproduction:`, JSON.stringify(baseLogData, null, 2));
        }
    }
    catch (error) {
        console.error(`${method} failed. Parameters for reproduction:`, JSON.stringify(baseLogData, null, 2));
        console.warn(`Failed to add user operation context to ${method} error log:`, error);
    }
}
